import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Loader2, Save, Building2, MapPin, Receipt, Shield, Upload, Trash2,
  CheckCircle2, AlertTriangle, Sparkles, Info,
  Zap, Bot, ShoppingCart, TestTube, FileText,
  Store, Utensils, Wrench, Scissors, Check, RotateCw, Eye, EyeOff,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { pedirExclusao } from '@/lib/confirmarExclusao'
import { useAuth } from '@/lib/auth'
import { buscarCep, codigoIbgeUf } from '@/lib/viacep'
import { getNav } from '@/components/layout/Sidebar'

interface Empresa {
  id: number
  razao_social: string
  tipo_operacao?: string
  nome_fantasia: string
  cnpj?: string
  ie?: string
  email: string
  telefone?: string
  cep?: string
  endereco?: string
  numero?: string
  complemento?: string
  bairro?: string
  cidade?: string
  uf?: string
  codigo_ibge_cidade?: string
  codigo_ibge_uf?: string
  codigo_pais?: string
  nome_pais?: string
  inscricao_municipal?: string
  cnae_fiscal?: string
  regime_tributario?: string
  crt?: string
  ambiente_nfe?: string
  serie_nfe?: string
  serie_nfce?: string
  nfe_proxima_numero?: number
  st_pf_ufs?: string
  csc_id?: string
  csc_token?: string
  natureza_operacao_padrao?: string
  certificado_pfx_path?: string
  certificado_configurado?: boolean
  plano?: string
  custo_overhead_pct?: number
}

interface CertInfo {
  subject: string
  issuer: string
  valido_desde: string
  valido_ate: string
  dias_restantes: number
  expirado: boolean
  proximo_expirar: boolean
}

const REGIMES = [
  { v: '', t: '— selecione —' },
  { v: 'simples_nacional', t: 'Simples Nacional' },
  { v: 'simples_excesso', t: 'Simples Nacional - Excesso de sublimite' },
  { v: 'regime_normal', t: 'Regime Normal (Lucro Presumido / Real)' },
  { v: 'mei', t: 'MEI' },
]
const CRT = [
  { v: '', t: '— selecione —' },
  { v: '1', t: '1 · Simples Nacional' },
  { v: '2', t: '2 · Simples Nacional - excesso sublimite' },
  { v: '3', t: '3 · Regime Normal' },
  { v: '4', t: '4 · MEI' },
]
// Regime e CRT são redundantes — escolher um deriva o outro (evita salvar só metade).
const REGIME_TO_CRT: Record<string, string> = {
  simples_nacional: '1', simples_excesso: '2', regime_normal: '3', mei: '4',
}
const CRT_TO_REGIME: Record<string, string> = {
  '1': 'simples_nacional', '2': 'simples_excesso', '3': 'regime_normal', '4': 'mei',
}
const AMBIENTES = [
  { v: '2', t: '2 · Homologação (testes)' },
  { v: '1', t: '1 · Produção (NFe real)' },
]

export default function ConfigEmpresaPage() {
  const qc = useQueryClient()
  const { user } = useAuth()
  const isAdmin = user?.perfil === 'admin' || user?.perfil === 'super_admin'
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<'dados' | 'endereco' | 'fiscal' | 'certificado' | 'integracoes' | 'modulos' | 'menu'>(
    params.get('ml') ? 'integracoes' : 'dados'
  )
  const [form, setForm] = useState<Empresa | null>(null)
  const [buscandoCep, setBuscandoCep] = useState(false)

  useEffect(() => {
    const ml = params.get('ml')
    if (ml === 'conectado') {
      toast.success('Mercado Livre conectado com sucesso!')
      qc.invalidateQueries({ queryKey: ['ml-status'] })
    } else if (ml === 'erro') {
      const motivo = params.get('motivo') || 'desconhecido'
      const detalhe = params.get('detalhe') || ''
      toast.error(`Falha ao conectar ML: ${motivo}${detalhe ? ' — ' + detalhe : ''}`, { duration: 10000 })
    }
    if (ml) {
      const p = new URLSearchParams(params)
      p.delete('ml'); p.delete('motivo'); p.delete('detalhe')
      setParams(p, { replace: true })
    }
  }, [params.get('ml')])

  const { data: empresa, isLoading } = useQuery<Empresa>({
    queryKey: ['empresa-atual'],
    queryFn: async () => (await api.get('/empresa/atual')).data,
  })

  const { data: certInfo } = useQuery<CertInfo>({
    queryKey: ['cert-info'],
    queryFn: async () => (await api.get('/empresa/atual/certificado/info')).data,
    enabled: !!empresa?.certificado_configurado,
    retry: false,
  })

  useEffect(() => {
    if (empresa) setForm(empresa)
  }, [empresa])

  const save = useMutation({
    mutationFn: async (d: Empresa) => (await api.put('/empresa/atual', d)).data,
    onSuccess: () => {
      toast.success('Empresa atualizada!')
      qc.invalidateQueries({ queryKey: ['empresa-atual'] })
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro'),
  })

  const removerCert = useMutation({
    mutationFn: async () => api.delete('/empresa/atual/certificado'),
    onSuccess: () => {
      toast.success('Certificado removido')
      qc.invalidateQueries({ queryKey: ['empresa-atual'] })
      qc.invalidateQueries({ queryKey: ['cert-info'] })
    },
  })

  async function autoPreencherCep() {
    if (!form?.cep) return
    setBuscandoCep(true)
    const data = await buscarCep(form.cep)
    setBuscandoCep(false)
    if (!data) { toast.error('CEP não encontrado'); return }
    setForm(p => p && ({
      ...p,
      endereco: data.logradouro || p.endereco,
      complemento: data.complemento || p.complemento,
      bairro: data.bairro || p.bairro,
      cidade: data.localidade || p.cidade,
      uf: data.uf || p.uf,
      codigo_ibge_cidade: data.ibge || p.codigo_ibge_cidade,
      codigo_ibge_uf: codigoIbgeUf(data.ibge) || p.codigo_ibge_uf,
    }))
    if (!data.logradouro) {
      // CEP único de cidade (ex.: interior) — ViaCEP não traz logradouro.
      toast('CEP de cidade: preencha o Logradouro à mão — a NF-e exige.', { icon: '⚠️', duration: 6000 })
    } else {
      toast.success('Endereço preenchido ✓')
    }
  }

  async function uploadCert(file: File, senha: string) {
    const fd = new FormData()
    fd.append('file', file)
    fd.append('senha', senha)
    const token = localStorage.getItem('pain_token')
    const r = await fetch('/api/empresa/atual/certificado', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: fd,
    })
    if (!r.ok) {
      const err = await r.json().catch(() => ({ detail: r.statusText }))
      throw new Error(err.detail || `HTTP ${r.status}`)
    }
    return r.json()
  }

  if (isLoading || !form) {
    return (
      <div className="flex items-center justify-center h-96 text-ink-400">
        <Loader2 className="animate-spin mr-2" /> Carregando...
      </div>
    )
  }

  const set = (k: keyof Empresa, v: any) => setForm(p => p && ({ ...p, [k]: v }))

  // Checklist: quais campos faltam pra NFe
  const checklistNFe = [
    { ok: !!form.cnpj && form.cnpj !== '00000000000000', label: 'CNPJ válido' },
    { ok: !!form.ie, label: 'Inscrição Estadual' },
    { ok: !!form.cep && !!form.endereco && !!form.numero && !!form.bairro && !!form.codigo_ibge_cidade, label: 'Endereço completo com IBGE' },
    { ok: !!form.regime_tributario && !!form.crt, label: 'Regime tributário + CRT' },
    { ok: !!form.ambiente_nfe && !!form.serie_nfe, label: 'Ambiente e série NFe' },
    { ok: !!form.certificado_configurado, label: 'Certificado A1 carregado' },
  ]
  const tudoOk = checklistNFe.every(c => c.ok)

  return (
    <div className="p-6 space-y-4 max-w-4xl mx-auto">
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <h1 className="text-2xl font-semibold text-ink-900 flex items-center gap-2">
            <Building2 size={22} /> Configuração da Empresa
          </h1>
          <p className="text-sm text-ink-500">
            Dados cadastrais, endereço, regime tributário e certificado digital para emissão de NF-e.
          </p>
        </div>
        <div className="text-right">
          <div className="text-xs text-ink-500">Status NF-e</div>
          {tudoOk ? (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-emerald-100 text-emerald-800">
              <CheckCircle2 size={12} /> Pronto pra emitir
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800">
              <AlertTriangle size={12} /> Pendências
            </span>
          )}
        </div>
      </div>

      {/* Checklist */}
      {!tudoOk && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h4 className="font-semibold text-amber-900 flex items-center gap-2 mb-2">
            <Info size={14} /> O que falta para emitir NF-e
          </h4>
          <ul className="space-y-1 text-sm">
            {checklistNFe.map((c, i) => (
              <li key={i} className="flex items-center gap-2">
                {c.ok ? <CheckCircle2 size={14} className="text-emerald-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
                <span className={c.ok ? 'text-ink-600 line-through' : 'text-ink-900'}>{c.label}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Tabs */}
      <div className="card">
        <div className="border-b flex px-5 bg-ink-50/50 overflow-x-auto">
          <TabBtn active={tab === 'dados'} onClick={() => setTab('dados')}>
            <Building2 size={14} /> Dados da empresa
          </TabBtn>
          <TabBtn active={tab === 'endereco'} onClick={() => setTab('endereco')}>
            <MapPin size={14} /> Endereço
          </TabBtn>
          <TabBtn active={tab === 'fiscal'} onClick={() => setTab('fiscal')}>
            <Receipt size={14} /> Fiscal / NFe
          </TabBtn>
          <TabBtn active={tab === 'certificado'} onClick={() => setTab('certificado')}>
            <Shield size={14} /> Certificado A1
          </TabBtn>
          <TabBtn active={tab === 'integracoes'} onClick={() => setTab('integracoes')}>
            <Zap size={14} /> Integrações
          </TabBtn>
          <TabBtn active={tab === 'modulos'} onClick={() => setTab('modulos')}>
            🧩 Módulos
          </TabBtn>
          <TabBtn active={tab === 'menu'} onClick={() => setTab('menu')}>
            <Eye size={14} /> Menu
          </TabBtn>
        </div>

        {tab === 'dados' && (
          <div className="p-5 space-y-4">
            {/* Seletor de tipo de operacao */}
            <TipoOperacaoSelector
              tipoAtual={form.tipo_operacao || 'completo'}
              onChange={(v) => set('tipo_operacao', v)}
            />

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Razão Social</label>
                <input className="input" value={form.razao_social}
                  onChange={e => set('razao_social', e.target.value)} />
              </div>
              <div>
                <label className="label">Nome Fantasia</label>
                <input className="input" value={form.nome_fantasia}
                  onChange={e => set('nome_fantasia', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">CNPJ <span className="text-red-500">*</span></label>
                <input className="input font-mono" value={form.cnpj || ''}
                  onChange={e => set('cnpj', e.target.value.replace(/\D/g, ''))}
                  placeholder="00000000000000" maxLength={14} />
              </div>
              <div>
                <label className="label">Inscrição Estadual <span className="text-red-500">*</span></label>
                <input className="input font-mono" value={form.ie || ''}
                  onChange={e => set('ie', e.target.value)} />
              </div>
              <div>
                <label className="label">Inscrição Municipal <span className="text-ink-400 font-normal">(opcional)</span></label>
                <input className="input font-mono" value={form.inscricao_municipal || ''}
                  onChange={e => set('inscricao_municipal', e.target.value)}
                  placeholder="só p/ quem emite NFS-e" />
                <p className="text-[11px] text-ink-500 mt-1">Usada na emissão de Nota de Serviço (NFS-e). Deixe vazio se não emite.</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">E-mail</label>
                <input className="input" type="email" value={form.email || ''}
                  onChange={e => set('email', e.target.value)} />
              </div>
              <div>
                <label className="label">Telefone</label>
                <input className="input" value={form.telefone || ''}
                  onChange={e => set('telefone', e.target.value)} />
              </div>
              <div>
                <label className="label">CNAE Fiscal</label>
                <input className="input font-mono" maxLength={7}
                  value={form.cnae_fiscal || ''}
                  onChange={e => set('cnae_fiscal', e.target.value.replace(/\D/g, ''))}
                  placeholder="0000000" />
              </div>
            </div>

            {/* Industrial: overhead */}
            <div className="border-t pt-4">
              <h3 className="text-sm font-semibold text-ink-700 mb-2">Custeio industrial</h3>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="label">Overhead de produção (%)</label>
                  <input type="number" step="0.01" className="input font-mono"
                    value={form.custo_overhead_pct ?? 0}
                    onChange={e => set('custo_overhead_pct', parseFloat(e.target.value) || 0)}
                    placeholder="0" />
                  <p className="text-xs text-ink-500 mt-1">
                    % aplicado sobre MP+MOD+Máquina no custo final de cada OP (energia, administrativo, etc.).
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'endereco' && (
          <div className="p-5 space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">CEP</label>
                <div className="flex gap-1">
                  <input className="input" value={form.cep || ''}
                    onChange={e => set('cep', e.target.value)} onBlur={autoPreencherCep}
                    placeholder="00000000" maxLength={9} />
                  <button type="button" onClick={autoPreencherCep}
                    disabled={buscandoCep || !form.cep}
                    className="px-2 bg-brand-100 hover:bg-brand-200 text-brand-700 rounded text-xs disabled:opacity-50">
                    {buscandoCep ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  </button>
                </div>
              </div>
              <div className="col-span-2">
                <label className="label">Logradouro</label>
                <input className="input" value={form.endereco || ''}
                  onChange={e => set('endereco', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="label">Número</label>
                <input className="input" value={form.numero || ''}
                  onChange={e => set('numero', e.target.value)} />
              </div>
              <div className="col-span-2">
                <label className="label">Complemento</label>
                <input className="input" value={form.complemento || ''}
                  onChange={e => set('complemento', e.target.value)} />
              </div>
              <div>
                <label className="label">Bairro</label>
                <input className="input" value={form.bairro || ''}
                  onChange={e => set('bairro', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2">
                <label className="label">Cidade</label>
                <input className="input" value={form.cidade || ''}
                  onChange={e => set('cidade', e.target.value)} />
              </div>
              <div>
                <label className="label">UF</label>
                <input className="input" maxLength={2} value={form.uf || ''}
                  onChange={e => set('uf', e.target.value.toUpperCase())} />
              </div>
              <div>
                <label className="label">Cód. IBGE cidade</label>
                <input className="input font-mono" maxLength={7}
                  value={form.codigo_ibge_cidade || ''}
                  onChange={e => set('codigo_ibge_cidade', e.target.value)} />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Cód. IBGE UF</label>
                <input className="input font-mono" maxLength={2}
                  value={form.codigo_ibge_uf || ''}
                  onChange={e => set('codigo_ibge_uf', e.target.value)} />
              </div>
              <div>
                <label className="label">Código país</label>
                <input className="input font-mono" value={form.codigo_pais || '1058'}
                  onChange={e => set('codigo_pais', e.target.value)} />
              </div>
              <div>
                <label className="label">Nome país</label>
                <input className="input" value={form.nome_pais || 'Brasil'}
                  onChange={e => set('nome_pais', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {tab === 'fiscal' && (
          <div className="p-5 space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
              💡 Defina regime tributário e CRT. Use <strong>ambiente 2 (Homologação)</strong> pra testes —
              as NFs não têm validade fiscal. Mude pra <strong>1 (Produção)</strong> só quando tudo estiver certo.
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Regime tributário <span className="text-red-500">*</span></label>
                <select className="input" value={form.regime_tributario || ''}
                  onChange={e => { const v = e.target.value; setForm(p => p && ({ ...p, regime_tributario: v, crt: REGIME_TO_CRT[v] ?? p.crt })) }}>
                  {REGIMES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">CRT <span className="text-red-500">*</span></label>
                <select className="input" value={form.crt || ''}
                  onChange={e => { const v = e.target.value; setForm(p => p && ({ ...p, crt: v, regime_tributario: CRT_TO_REGIME[v] ?? p.regime_tributario })) }}>
                  {CRT.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="label">Ambiente NFe <span className="text-red-500">*</span></label>
                <select className="input" value={form.ambiente_nfe || '2'}
                  onChange={e => set('ambiente_nfe', e.target.value)}>
                  {AMBIENTES.map(o => <option key={o.v} value={o.v}>{o.t}</option>)}
                </select>
              </div>
              <div>
                <label className="label">Série NF-e</label>
                <input className="input font-mono" maxLength={3}
                  value={form.serie_nfe || '1'} onChange={e => set('serie_nfe', e.target.value)} />
              </div>
              <div>
                <label className="label">Série NFC-e</label>
                <input className="input font-mono" maxLength={3}
                  value={form.serie_nfce || '1'} onChange={e => set('serie_nfce', e.target.value)} />
              </div>
            </div>
            <div className="pt-3 border-t">
              <h5 className="text-xs font-bold text-ink-500 uppercase mb-2">Numeração da NF-e</h5>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="label">Próximo número da NF-e</label>
                  <input className="input font-mono" type="number" min={1}
                    value={form.nfe_proxima_numero ?? ''}
                    disabled={!isAdmin}
                    onChange={e => set('nfe_proxima_numero', e.target.value === '' ? undefined : parseInt(e.target.value, 10))}
                    placeholder="1" />
                </div>
                <p className="text-xs text-ink-500 pb-2">
                  {isAdmin
                    ? '⚠️ É o número que a PRÓXIMA NF-e vai usar (série acima). Só ajuste pra continuar de onde a emissão parou — número repetido é rejeitado pela SEFAZ.'
                    : '🔒 Apenas admin pode alterar a numeração fiscal.'}
                </p>
              </div>
            </div>
            <div>
              <label className="label">Natureza da operação padrão</label>
              <input className="input" value={form.natureza_operacao_padrao || ''}
                onChange={e => set('natureza_operacao_padrao', e.target.value)}
                placeholder="Venda de mercadoria" />
            </div>
            <div className="pt-3 border-t">
              <h5 className="text-xs font-bold text-ink-500 uppercase mb-2">NFC-e (cupom fiscal)</h5>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label">CSC ID</label>
                  <input className="input font-mono" value={form.csc_id || ''}
                    onChange={e => set('csc_id', e.target.value)} placeholder="000001" />
                </div>
                <div>
                  <label className="label">CSC Token</label>
                  <input className="input font-mono" value={form.csc_token || ''}
                    onChange={e => set('csc_token', e.target.value)} type="password" />
                </div>
              </div>
              <p className="text-xs text-ink-500 mt-1">
                Obtenha o CSC no portal da SEFAZ estadual. Necessário pra emitir NFC-e (cupom).
              </p>
            </div>
          </div>
        )}

        {tab === 'certificado' && (
          <div className="p-5 space-y-4">
            {form.certificado_configurado ? (
              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-5 space-y-3">
                <div className="flex items-center gap-2 text-emerald-900">
                  <CheckCircle2 size={20} />
                  <h4 className="font-semibold">Certificado A1 carregado</h4>
                </div>
                {certInfo && (
                  <div className="text-sm space-y-1 font-mono text-emerald-900 bg-white rounded p-3">
                    <div><span className="text-emerald-600 font-sans">Emitido para:</span> {certInfo.subject}</div>
                    <div><span className="text-emerald-600 font-sans">Emissor:</span> {certInfo.issuer}</div>
                    <div><span className="text-emerald-600 font-sans">Válido até:</span> {new Date(certInfo.valido_ate).toLocaleString('pt-BR')}</div>
                    <div>
                      <span className="text-emerald-600 font-sans">Dias restantes:</span>{' '}
                      <span className={certInfo.proximo_expirar ? 'text-red-700 font-bold' : ''}>
                        {certInfo.dias_restantes}
                      </span>
                    </div>
                  </div>
                )}
                {certInfo?.proximo_expirar && (
                  <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 flex items-center gap-2">
                    <AlertTriangle size={14} /> Certificado vence em menos de 30 dias! Renove com urgência.
                  </div>
                )}
                <button onClick={() => pedirExclusao({ descricao: 'o certificado', onConfirm: () => removerCert.mutate() })}
                  className="btn-secondary flex items-center gap-1 text-red-700 hover:bg-red-50">
                  <Trash2 size={14} /> Remover certificado
                </button>
              </div>
            ) : (
              <UploadCertificado onUpload={uploadCert} onSuccess={() => qc.invalidateQueries({ queryKey: ['empresa-atual'] })} />
            )}
          </div>
        )}

        {tab === 'integracoes' && <TabIntegracoes />}
        {tab === 'modulos' && <TabModulos empresa={empresa} />}
        {tab === 'menu' && <TabMenu empresa={empresa} />}
      </div>

      {/* Botão salvar */}
      <div className="flex justify-end">
        <button onClick={() => form && save.mutate(form)} disabled={save.isPending}
          className="btn-primary">
          {save.isPending ? <><Loader2 size={14} className="animate-spin" /> Salvando...</>
            : <><Save size={14} /> Salvar alterações</>}
        </button>
      </div>
    </div>
  )
}

// ============ TAB BUTTON ============
function TabBtn({ active, onClick, children }: any) {
  return (
    <button onClick={onClick}
      className={`px-4 py-2 text-sm font-medium border-b-2 flex items-center gap-1 whitespace-nowrap ${
        active ? 'border-brand-600 text-brand-700' : 'border-transparent text-ink-500'
      }`}>
      {children}
    </button>
  )
}

// ============ UPLOAD CERTIFICADO ============
function UploadCertificado({ onUpload, onSuccess }: any) {
  const [file, setFile] = useState<File | null>(null)
  const [senha, setSenha] = useState('')
  const [uploading, setUploading] = useState(false)

  async function handleSubmit() {
    if (!file || !senha) { toast.error('Escolha o arquivo e digite a senha'); return }
    setUploading(true)
    try {
      await onUpload(file, senha)
      toast.success('Certificado carregado com sucesso!')
      setFile(null); setSenha('')
      onSuccess?.()
    } catch (e: any) {
      toast.error(e.message || 'Erro no upload')
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="space-y-3">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        🔐 <strong>Certificado Digital A1</strong>: arquivo <code className="bg-blue-100 px-1">.pfx</code> ou
        <code className="bg-blue-100 px-1 ml-1">.p12</code> emitido pela Receita Federal (AC Serasa, Certisign, etc).
        Válido por 1 ano. Sem ele, é impossível emitir NF-e.
      </div>

      <label className="block border-2 border-dashed border-ink-300 rounded-lg p-8 text-center cursor-pointer hover:border-ink-400 bg-ink-50/50">
        <input type="file" accept=".pfx,.p12" className="hidden"
          onChange={e => setFile(e.target.files?.[0] || null)} />
        <Upload size={28} className="mx-auto text-ink-400 mb-2" />
        {file ? (
          <p className="text-sm">
            <strong className="text-brand-600">{file.name}</strong>
            <span className="text-ink-400"> ({(file.size / 1024).toFixed(0)} KB)</span>
          </p>
        ) : (
          <>
            <p className="text-sm text-ink-600">
              <strong>Clique para selecionar</strong> o arquivo do certificado
            </p>
            <p className="text-xs text-ink-400 mt-1">.pfx ou .p12 · máx 5MB</p>
          </>
        )}
      </label>

      <div>
        <label className="label">Senha do certificado</label>
        <input type="password" className="input" value={senha}
          autoComplete="new-password" name="cert-senha" data-lpignore="true" data-1p-ignore
          onChange={e => setSenha(e.target.value)}
          placeholder="Senha que você definiu ao emitir o certificado" />
      </div>

      <button onClick={handleSubmit} disabled={!file || !senha || uploading}
        className="btn-primary">
        {uploading ? <><Loader2 size={14} className="animate-spin" /> Validando e enviando...</>
          : <><Shield size={14} /> Carregar certificado</>}
      </button>
    </div>
  )
}


// ============ TAB INTEGRACOES ============
interface Integracoes {
  fiscal_provider: string | null
  fiscal_ambiente: string | null
  fiscal_api_url: string | null
  fiscal_token_configurado: boolean
  fiscal_token_preview: string | null
  ia_modelo_padrao: string | null
  ia_openai_configurado: boolean
  ia_anthropic_configurado: boolean
  ia_gemini_configurado: boolean
  ml_app_id: string | null
  ml_client_secret_configurado: boolean
  ml_conectado: boolean
  ml_user_id: string | null
  tray_consumer_key_configurado: boolean
  tray_consumer_secret_configurado: boolean
  tray_loja_url: string | null
  tray_conectado: boolean
  tray_store_id: string | null
  tiny_token_configurado: boolean
  tiny_sync_ativo: boolean
  tiny_ultima_sync: string | null
}

// ====================== TAB: MÓDULOS ATIVOS ======================
const MODULOS_DISPONIVEIS: { key: string; label: string; descricao: string; icon: string }[] = [
  { key: 'vendas',      label: 'Vendas',      descricao: 'Pedidos, fluxo de venda, faturamento', icon: '🛒' },
  { key: 'estoque',     label: 'Estoque',     descricao: 'Movimentações, depósitos, inventário', icon: '📦' },
  { key: 'financeiro',  label: 'Financeiro',  descricao: 'Contas a receber/pagar, lançamentos, conciliação', icon: '💰' },
  { key: 'fiscal',      label: 'Fiscal',      descricao: 'NFe, NFCe, livros fiscais, apuração', icon: '📑' },
  { key: 'industrial',  label: 'Industrial',  descricao: 'Produção, OPs, fichas técnicas, manutenção', icon: '🏭' },
  { key: 'marketplace', label: 'Marketplace', descricao: 'Mercado Livre, Mercado Pago, integrações de venda online (esconde colunas/chips ML/MP)', icon: '🌐' },
  { key: 'servicos',    label: 'Serviços (OS)', descricao: 'Ordens de serviço, portal público, segmentos', icon: '🔧' },
  { key: 'roteirizacao', label: 'Roteirização', descricao: 'Montagem de rotas de entrega e otimização', icon: '🗺️' },
  { key: 'comodato',    label: 'Comodato',    descricao: 'Equipamentos em comodato, freezers, contratos', icon: '❄️' },
  { key: 'sac',         label: 'SAC',         descricao: 'Atendimento, reclamações e pós-venda', icon: '🎧' },
  { key: 'controladoria', label: 'Controladoria', descricao: 'Indicadores executivos e análises gerenciais', icon: '📈' },
  { key: 'rh',          label: 'RH · Pessoal', descricao: 'Funcionários, ponto, folha', icon: '👥' },
  { key: 'loja',        label: 'Loja Online',  descricao: 'E-commerce próprio, vitrine, checkout', icon: '🛍️' },
  { key: 'ia',          label: 'Sofia', descricao: 'Sofia: chat, sugestões, automação inteligente', icon: '✨' },
  { key: 'bpm',         label: 'BPM / Processos', descricao: 'Workflows, fluxos aprovação, designer', icon: '⚙️' },
  { key: 'intranet',    label: 'Intranet',    descricao: 'Feed interno, comunicados, páginas', icon: '🏢' },
  { key: 'relatorios',  label: 'Relatórios',  descricao: 'DRE, fluxo de caixa, visão do dono', icon: '📊' },
]

function TabModulos({ empresa }: { empresa: any }) {
  const qc = useQueryClient()
  const [modulos, setModulos] = useState<Record<string, boolean>>(() =>
    (empresa?.modulos_ativos as Record<string, boolean>) || {}
  )
  const [salvo, setSalvo] = useState(false)

  const salvar = useMutation({
    mutationFn: async () => {
      await api.put('/empresa/atual', { modulos_ativos: modulos })
      return modulos
    },
    onSuccess: (enviados) => {
      // Usa o estado LOCAL (o que enviamos) — backend pode não devolver modulos_ativos no response
      try {
        const u = localStorage.getItem('pain_empresa')
        if (u) {
          const e = JSON.parse(u)
          e.modulos_ativos = enviados
          localStorage.setItem('pain_empresa', JSON.stringify(e))
        }
      } catch {}
      qc.invalidateQueries()
      toast.success('Módulos atualizados — recarregando...')
      setSalvo(true)
      setTimeout(() => window.location.reload(), 1200)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const toggle = (key: string) => {
    setModulos(prev => ({ ...prev, [key]: prev[key] === false ? true : false }))
  }
  const isAtivo = (key: string) => modulos[key] !== false

  return (
    <div className="p-5 space-y-4 max-w-3xl">
      <div className="bg-amber-50 border border-amber-200 rounded p-3 text-sm text-amber-900">
        Desative módulos que sua empresa não usa pra <strong>esconder colunas, chips, badges e itens de menu</strong> relacionados.
        Ex: empresa que não vende em marketplace → desliga "Marketplace" e some Pedido ML, ID Mercado Pago, chips Flex/Hoje, etc.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {MODULOS_DISPONIVEIS.map(m => {
          const ativo = isAtivo(m.key)
          return (
            <button key={m.key} type="button" onClick={() => toggle(m.key)}
              className={`text-left p-3 rounded-lg border-2 transition flex items-start gap-3 ${
                ativo
                  ? 'border-emerald-300 bg-emerald-50 hover:bg-emerald-100'
                  : 'border-ink-200 bg-ink-50 hover:bg-ink-100 opacity-70'
              }`}>
              <div className="text-2xl">{m.icon}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-ink-900">{m.label}</span>
                  {ativo
                    ? <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-emerald-600 text-white">ATIVO</span>
                    : <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-ink-300 text-white">DESLIGADO</span>}
                </div>
                <div className="text-xs text-ink-600 mt-0.5">{m.descricao}</div>
              </div>
              <div className={`w-10 h-6 rounded-full p-0.5 transition ${ativo ? 'bg-emerald-500' : 'bg-ink-300'}`}>
                <div className={`w-5 h-5 rounded-full bg-white transition-transform ${ativo ? 'translate-x-4' : ''}`} />
              </div>
            </button>
          )
        })}
      </div>

      <div className="flex justify-end gap-2 pt-3 border-t">
        <button onClick={() => setModulos((empresa?.modulos_ativos as any) || {})}
          className="btn-secondary" disabled={salvar.isPending}>
          Cancelar
        </button>
        <button onClick={() => salvar.mutate()}
          className="btn-primary" disabled={salvar.isPending || salvo}>
          {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar e recarregar'}
        </button>
      </div>
    </div>
  )
}


// FEATURE 01/07: aba "Menu" — esconde/mostra itens do menu lateral globalmente
// (por empresa, vale pra TODOS os usuários). Persiste em empresa.menu_oculto (lista
// de rotas) via PUT /empresa/atual. Espelha o catálogo do menu (getNav), igual à
// tela de Perfis. Diferente de Módulos (liga/desliga módulo inteiro) e de Perfis
// de Acesso (permissão por perfil). O Sidebar aplica via filtrarOcultos().
function TabMenu({ empresa }: { empresa: any }) {
  const qc = useQueryClient()

  // Catálogo = espelho do menu. Pula super/adminOnly (ex.: a própria Configurações)
  // pra ninguém se trancar pra fora. Dashboard "/" fica de fora (é a home).
  const grupos = (() => {
    const nav = getNav(false, {}, 0, empresa?.id ?? null, 0, 0)
    const avulsos: { to: string; label: string }[] = []
    const out: { label: string; items: { to: string; label: string }[] }[] = []
    for (const item of nav) {
      if (item.superOnly || item.adminOnly) continue
      if (item.children?.length) {
        out.push({ label: item.label, items: item.children.map((c: any) => ({ to: c.to, label: c.label })) })
      } else if (item.to !== '/') {
        avulsos.push({ to: item.to, label: item.label })
      }
    }
    if (avulsos.length) out.unshift({ label: 'Itens principais', items: avulsos })
    return out
  })()

  const [ocultos, setOcultos] = useState<Set<string>>(() => new Set<string>(empresa?.menu_oculto || []))
  const [salvo, setSalvo] = useState(false)

  const salvar = useMutation({
    mutationFn: async () => {
      const lista = Array.from(ocultos)
      await api.put('/empresa/atual', { menu_oculto: lista })
      return lista
    },
    onSuccess: (lista) => {
      // Atualiza o cache local da empresa (o Sidebar lê do useAuth/localStorage).
      try {
        const u = localStorage.getItem('pain_empresa')
        if (u) {
          const e = JSON.parse(u)
          e.menu_oculto = lista
          localStorage.setItem('pain_empresa', JSON.stringify(e))
        }
      } catch {}
      qc.invalidateQueries()
      toast.success('Menu atualizado — recarregando...')
      setSalvo(true)
      setTimeout(() => window.location.reload(), 1000)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  const visivel = (to: string) => !ocultos.has(to)
  const toggle = (to: string) => setOcultos(prev => {
    const n = new Set(prev)
    if (n.has(to)) n.delete(to); else n.add(to)
    return n
  })

  return (
    <div className="p-5 space-y-4 max-w-3xl">
      <div className="bg-sky-50 border border-sky-200 rounded p-3 text-sm text-sky-900">
        Ligue/desligue o que aparece no <strong>menu lateral</strong>. Itens desligados
        somem do menu pra todos os usuários (a tela continua acessível por link direto).
        Para restringir por perfil, use <strong>Perfis de Acesso</strong>.
      </div>

      {grupos.map(g => (
        <div key={g.label} className="border rounded-lg overflow-hidden">
          <div className="px-3 py-2 bg-ink-50 border-b font-semibold text-sm text-ink-800">{g.label}</div>
          <div className="divide-y">
            {g.items.map(it => {
              const on = visivel(it.to)
              return (
                <button key={it.to} type="button" onClick={() => toggle(it.to)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-ink-50 transition">
                  {on ? <Eye size={16} className="text-emerald-600 shrink-0" />
                      : <EyeOff size={16} className="text-ink-400 shrink-0" />}
                  <span className={`flex-1 text-sm ${on ? 'text-ink-900' : 'text-ink-400 line-through'}`}>
                    {it.label}
                  </span>
                  <span className="text-[11px] text-ink-400 font-mono hidden sm:inline">{it.to}</span>
                  <div className={`w-10 h-6 rounded-full p-0.5 transition shrink-0 ${on ? 'bg-emerald-500' : 'bg-ink-300'}`}>
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${on ? 'translate-x-4' : ''}`} />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      ))}

      <div className="flex justify-end gap-2 pt-3 border-t">
        <button onClick={() => setOcultos(new Set(empresa?.menu_oculto || []))}
          className="btn-secondary" disabled={salvar.isPending}>
          Cancelar
        </button>
        <button onClick={() => salvar.mutate()}
          className="btn-primary" disabled={salvar.isPending || salvo}>
          {salvar.isPending ? <Loader2 size={14} className="animate-spin" /> : 'Salvar e recarregar'}
        </button>
      </div>
    </div>
  )
}


function TabIntegracoes() {
  const qc = useQueryClient()
  const [form, setForm] = useState<any>({})
  const [editandoFiscal, setEditandoFiscal] = useState(false)
  const [editandoOpenai, setEditandoOpenai] = useState(false)
  const [editandoAnth, setEditandoAnth] = useState(false)
  const [editandoGemini, setEditandoGemini] = useState(false)
  const [editandoMLSecret, setEditandoMLSecret] = useState(false)
  const [editandoTrayKey, setEditandoTrayKey] = useState(false)
  const [editandoTraySecret, setEditandoTraySecret] = useState(false)
  const [editandoTinyToken, setEditandoTinyToken] = useState(false)
  const [testandoFiscal, setTestandoFiscal] = useState(false)

  const { data: integ, isLoading } = useQuery<Integracoes>({
    queryKey: ['integracoes'],
    queryFn: async () => (await api.get('/empresa/atual/integracoes')).data,
  })

  useEffect(() => {
    if (integ) {
      setForm({
        fiscal_provider: integ.fiscal_provider || 'focus',
        fiscal_ambiente: integ.fiscal_ambiente || 'homologacao',
        fiscal_api_url: integ.fiscal_api_url || '',
        ia_modelo_padrao: integ.ia_modelo_padrao || '',
        ml_app_id: integ.ml_app_id || '',
        tray_loja_url: integ.tray_loja_url || '',
      })
    }
  }, [integ])

  const save = useMutation({
    mutationFn: async (payload: any) => (await api.put('/empresa/atual/integracoes', payload)).data,
    onSuccess: () => {
      toast.success('Integrações salvas!')
      qc.invalidateQueries({ queryKey: ['integracoes'] })
      setEditandoFiscal(false); setEditandoOpenai(false); setEditandoAnth(false)
      setEditandoGemini(false); setEditandoMLSecret(false)
      setEditandoTrayKey(false); setEditandoTraySecret(false)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Erro ao salvar'),
  })

  async function testarFiscal() {
    setTestandoFiscal(true)
    try {
      const r = await api.post('/empresa/atual/integracoes/fiscal/testar')
      if (r.data.ok) {
        toast.success(`Conexão OK! ${r.data.mensagem}`)
      } else {
        toast.error(`Falha: ${r.data.mensagem}`)
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao testar')
    } finally {
      setTestandoFiscal(false)
    }
  }

  if (isLoading || !integ) {
    return <div className="p-5 text-center text-ink-400"><Loader2 className="animate-spin inline" /> Carregando...</div>
  }

  return (
    <div className="p-5 space-y-5">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900">
        🔐 Tokens e chaves de API são criptografados com Fernet antes de salvar no banco.
        Nenhum token é retornado em texto plano pela API — apenas flags de status.
      </div>

      {/* ========== FISCAL (Focus NFe) ========== */}
      <SecaoIntegracao
        icone={<FileText size={18} className="text-blue-600" />}
        titulo="Emissão Fiscal (Focus NFe)"
        descricao="Provedor de emissão de NF-e e NFC-e"
        conectado={integ.fiscal_token_configurado}
        info={integ.fiscal_token_configurado ? `Token: ${integ.fiscal_token_preview} · ${integ.fiscal_ambiente}` : undefined}
      >
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Provider</label>
              <select className="input" value={form.fiscal_provider}
                onChange={e => setForm((f: any) => ({ ...f, fiscal_provider: e.target.value }))}>
                <option value="focus">Focus NFe</option>
                <option value="plugnotas">PlugNotas</option>
                <option value="mock">Mock (dev)</option>
              </select>
            </div>
            <div>
              <label className="label">Ambiente</label>
              <select className="input" value={form.fiscal_ambiente}
                onChange={e => setForm((f: any) => ({ ...f, fiscal_ambiente: e.target.value }))}>
                <option value="homologacao">Homologação (testes)</option>
                <option value="producao">Produção (NFe real)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">URL customizada (opcional)</label>
            <input className="input font-mono text-xs" value={form.fiscal_api_url || ''}
              onChange={e => setForm((f: any) => ({ ...f, fiscal_api_url: e.target.value }))}
              placeholder="deixe vazio para usar padrão do provider" autoComplete="off" />
          </div>
          <div>
            <label className="label">Token API {integ.fiscal_token_configurado && !editandoFiscal && <span className="text-xs text-emerald-600">(configurado)</span>}</label>
            {editandoFiscal || !integ.fiscal_token_configurado ? (
              <input className="input font-mono text-xs" type="password" autoComplete="new-password"
                value={form.fiscal_api_token || ''}
                onChange={e => setForm((f: any) => ({ ...f, fiscal_api_token: e.target.value }))}
                placeholder="Cole aqui o token Focus NFe" />
            ) : (
              <div className="flex items-center gap-2">
                <input className="input font-mono text-xs bg-ink-50" readOnly value={integ.fiscal_token_preview || ''} />
                <button type="button" onClick={() => setEditandoFiscal(true)}
                  className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded">Alterar</button>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => {
              const payload: any = {
                fiscal_provider: form.fiscal_provider,
                fiscal_ambiente: form.fiscal_ambiente,
                fiscal_api_url: form.fiscal_api_url || '',
              }
              if (form.fiscal_api_token !== undefined) payload.fiscal_api_token = form.fiscal_api_token
              save.mutate(payload)
            }}
              className="btn-primary flex-1">
              <Save size={14} /> Salvar fiscal
            </button>
            {integ.fiscal_token_configurado && (
              <button onClick={testarFiscal} disabled={testandoFiscal}
                className="btn-secondary">
                {testandoFiscal ? <Loader2 className="animate-spin" size={14} /> : <TestTube size={14} />}
                Testar conexão
              </button>
            )}
          </div>
          <p className="text-xs text-ink-500">
            📚 Pegue seu token em <a href="https://app.focusnfe.com.br" target="_blank" rel="noreferrer" className="text-brand-600 hover:underline">app.focusnfe.com.br</a>
          </p>
        </div>
      </SecaoIntegracao>

      {/* ========== SOFIA (IA) — provedores em cascata + memória própria ========== */}
      <SecaoSofiaIA />

      {/* ========== MERCADO LIVRE ========== */}
      <SecaoMercadoLivre integ={integ} form={form} setForm={setForm} save={save}
        editandoMLSecret={editandoMLSecret} setEditandoMLSecret={setEditandoMLSecret} />

      {/* ========== TRAY ========== */}
      <SecaoTray integ={integ} form={form} setForm={setForm} save={save}
        editandoTrayKey={editandoTrayKey} setEditandoTrayKey={setEditandoTrayKey}
        editandoTraySecret={editandoTraySecret} setEditandoTraySecret={setEditandoTraySecret} />

      {/* ========== TINY ERP ========== */}
      <SecaoTiny integ={integ} form={form} setForm={setForm} save={save}
        editandoTinyToken={editandoTinyToken} setEditandoTinyToken={setEditandoTinyToken} />
      {false && <>{/* placeholder-legacy-removido */}</>}
    </div>
  )
}

function SecaoIntegracao({ icone, titulo, descricao, conectado, info, children }: any) {
  return (
    <div className="border border-ink-200 rounded-lg overflow-hidden">
      <div className="bg-ink-50 border-b border-ink-200 px-4 py-3 flex items-center gap-3">
        {icone}
        <div className="flex-1">
          <h3 className="font-semibold text-sm">{titulo}</h3>
          <p className="text-xs text-ink-500">{descricao}</p>
          {info && <p className="text-xs text-ink-600 font-mono mt-0.5">{info}</p>}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
          conectado ? 'bg-emerald-100 text-emerald-800' : 'bg-ink-100 text-ink-500'
        }`}>
          {conectado ? '✓ Configurado' : 'Não configurado'}
        </span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  )
}

function TokenInput({ label, placeholder, configurado, editando, onEditar, valor, onChange, help }: any) {
  return (
    <div>
      <label className="label">{label} {configurado && !editando && <span className="text-xs text-emerald-600">(configurado)</span>}</label>
      {editando || !configurado ? (
        <input className="input font-mono text-xs" type="password" autoComplete="new-password"
          value={valor || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder} />
      ) : (
        <div className="flex items-center gap-2">
          <input className="input font-mono text-xs bg-ink-50" readOnly value="••••••••••••••••" />
          <button type="button" onClick={onEditar}
            className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded">Alterar</button>
        </div>
      )}
      {help && <p className="text-xs text-ink-500 mt-1">{help}</p>}
    </div>
  )
}

// ============ SECAO MERCADO LIVRE (OAuth) ============
function SecaoMercadoLivre({ integ, form, setForm, save, editandoMLSecret, setEditandoMLSecret }: any) {
  const qc = useQueryClient()
  const [testando, setTestando] = useState(false)
  const [conectando, setConectando] = useState(false)

  const { data: mlStatus } = useQuery<any>({
    queryKey: ['ml-status'],
    queryFn: async () => (await api.get('/ml/status')).data,
    refetchInterval: 10000,
  })

  async function conectarML() {
    setConectando(true)
    try {
      const r = await api.get('/ml/conectar')
      if (r.data.url_autorizacao) {
        window.location.href = r.data.url_autorizacao
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail?.mensagem || e?.response?.data?.detail || 'Erro')
      setConectando(false)
    }
  }

  async function desconectarML() {
    if (!confirm('Desconectar ML? Os anuncios nao serao mais sincronizados.')) return
    try {
      await api.post('/ml/desconectar')
      toast.success('ML desconectado')
      qc.invalidateQueries({ queryKey: ['ml-status'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function testarML() {
    setTestando(true)
    try {
      const r = await api.post('/ml/testar')
      if (r.data.ok) {
        toast.success(`Conectado como ${r.data.nickname}`)
      } else {
        toast.error(r.data.erro || 'Falha')
      }
    } finally {
      setTestando(false)
    }
  }

  const jaConectadoML = mlStatus?.conectado
  const credenciaisOk = integ.ml_app_id && integ.ml_client_secret_configurado

  return (
    <div className="border border-ink-200 rounded-lg overflow-hidden">
      <div className="bg-ink-50 border-b border-ink-200 px-4 py-3 flex items-center gap-3">
        <ShoppingCart size={18} className="text-yellow-600" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Mercado Livre</h3>
          <p className="text-xs text-ink-500">Integração com marketplace via OAuth 2.0</p>
          {jaConectadoML && (
            <p className="text-xs text-ink-600 font-mono mt-0.5">
              Conectado como {mlStatus.ml_nickname} ({mlStatus.ml_country} · user {mlStatus.ml_user_id})
            </p>
          )}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
          jaConectadoML ? 'bg-emerald-100 text-emerald-800'
          : credenciaisOk ? 'bg-amber-100 text-amber-800'
          : 'bg-ink-100 text-ink-500'
        }`}>
          {jaConectadoML ? '✓ Conectado' : credenciaisOk ? 'Pronto p/ conectar' : 'Credenciais faltando'}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-900">
          💡 <strong>Passos:</strong><br/>
          1. Crie um App em <a href="https://developers.mercadolibre.com/devcenter" target="_blank" rel="noreferrer" className="text-brand-600 underline">developers.mercadolibre.com</a><br/>
          2. Configure Redirect URI: <code className="bg-blue-100 px-1">https://app.velore-erp.com.br/api/ml/callback</code><br/>
          3. Cole App ID + Client Secret abaixo e salve<br/>
          4. Clique em <strong>"Conectar Mercado Livre"</strong> e autorize no site do ML
        </div>

        <div>
          <label className="label">App ID</label>
          <input className="input font-mono" value={form.ml_app_id || ''}
            onChange={e => setForm((f: any) => ({ ...f, ml_app_id: e.target.value }))}
            placeholder="1234567890" />
        </div>

        <TokenInput
          label="Client Secret"
          placeholder="seu client secret"
          configurado={integ.ml_client_secret_configurado}
          editando={editandoMLSecret}
          onEditar={() => setEditandoMLSecret(true)}
          valor={form.ml_client_secret}
          onChange={(v: string) => setForm((f: any) => ({ ...f, ml_client_secret: v }))}
          help={<>Veja "Credenciais" no seu App no <a href="https://developers.mercadolibre.com/devcenter" target="_blank" rel="noreferrer" className="text-brand-600 underline">DevCenter</a></>}
        />

        <div className="flex gap-2">
          <button onClick={() => {
            const payload: any = { ml_app_id: form.ml_app_id }
            if (form.ml_client_secret !== undefined) payload.ml_client_secret = form.ml_client_secret
            save.mutate(payload)
          }}
            className="btn-primary flex-1">
            <Save size={14} /> Salvar credenciais
          </button>
        </div>

        <div className="pt-3 border-t border-ink-200 flex gap-2">
          {jaConectadoML ? (
            <>
              <button onClick={testarML} disabled={testando}
                className="btn-secondary flex-1">
                {testando ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />}
                Testar conexão
              </button>
              <button onClick={desconectarML}
                className="btn-secondary text-red-700 hover:bg-red-50 flex-1">
                Desconectar
              </button>
            </>
          ) : (
            <button onClick={conectarML}
              disabled={!credenciaisOk || conectando}
              className="btn-primary w-full bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-500">
              {conectando ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
              Conectar Mercado Livre
            </button>
          )}
        </div>

        {!credenciaisOk && (
          <p className="text-xs text-ink-500">
            Salve App ID + Client Secret antes de conectar.
          </p>
        )}
      </div>
    </div>
  )
}


function SecaoTray({ integ, form, setForm, save, editandoTrayKey, setEditandoTrayKey, editandoTraySecret, setEditandoTraySecret }: any) {
  const qc = useQueryClient()
  const [testando, setTestando] = useState(false)
  const [conectando, setConectando] = useState(false)
  const [sincronizando, setSincronizando] = useState(false)

  const { data: trayStatus } = useQuery<any>({
    queryKey: ['tray-status'],
    queryFn: async () => (await api.get('/tray/status')).data,
    refetchInterval: 10000,
  })

  async function conectarTray() {
    setConectando(true)
    try {
      const params = form.tray_loja_url ? { store_url: form.tray_loja_url } : undefined
      const r = await api.get('/tray/conectar-url', { params })
      if (!r.data?.url) throw new Error('URL de autorização não retornada')
      window.location.href = r.data.url
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || e?.message || 'Erro')
      setConectando(false)
    }
  }

  async function desconectarTray() {
    if (!confirm('Desconectar Tray? Os pedidos não serão mais sincronizados.')) return
    try {
      await api.post('/tray/desconectar')
      toast.success('Tray desconectada')
      qc.invalidateQueries({ queryKey: ['tray-status'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function testarTray() {
    setTestando(true)
    try {
      const r = await api.post('/tray/testar')
      if (r.data.ok) {
        toast.success(`Loja ${r.data.store_id} OK · ${r.data.total_pedidos ?? '?'} pedidos`)
      } else {
        toast.error(r.data.erro || 'Falha')
      }
    } finally {
      setTestando(false)
    }
  }

  async function sincronizarTray() {
    setSincronizando(true)
    try {
      const r = await api.post('/tray/sincronizar-pedidos')
      toast.success(`${r.data.pedidos_criados} novos · ${r.data.ja_existiam} já existiam · ${r.data.pedidos_baixados} baixados`)
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao sincronizar')
    } finally {
      setSincronizando(false)
    }
  }

  const jaConectado = trayStatus?.conectado
  const credenciaisOk = integ.tray_consumer_key_configurado && integ.tray_consumer_secret_configurado && integ.tray_loja_url

  return (
    <div className="border border-ink-200 rounded-lg overflow-hidden">
      <div className="bg-ink-50 border-b border-ink-200 px-4 py-3 flex items-center gap-3">
        <ShoppingCart size={18} className="text-pink-600" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Tray Commerce</h3>
          <p className="text-xs text-ink-500">Integração com loja Tray (e-commerce) via OAuth</p>
          {jaConectado && (
            <p className="text-xs text-ink-600 font-mono mt-0.5">
              Loja {trayStatus.store_id} · {trayStatus.eventos_pendentes} eventos pendentes
            </p>
          )}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
          jaConectado ? 'bg-emerald-100 text-emerald-800'
          : credenciaisOk ? 'bg-amber-100 text-amber-800'
          : 'bg-ink-100 text-ink-500'
        }`}>
          {jaConectado ? '✓ Conectado' : credenciaisOk ? 'Pronto p/ conectar' : 'Credenciais faltando'}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-900">
          💡 <strong>Passos:</strong><br/>
          1. Crie um App em <a href="http://developers.tray.com.br/" target="_blank" rel="noreferrer" className="text-brand-600 underline">developers.tray.com.br</a><br/>
          2. Configure URL de callback: <code className="bg-blue-100 px-1">https://app.velore-erp.com.br/api/tray/callback</code><br/>
          3. Configure URL de notificação: <code className="bg-blue-100 px-1">https://app.velore-erp.com.br/api/tray/notificacao</code><br/>
          4. Cole Consumer Key + Secret + URL da loja abaixo e salve<br/>
          5. Clique em <strong>"Conectar Tray"</strong> e autorize na sua loja
        </div>

        <div>
          <label className="label">URL da loja Tray</label>
          <input className="input font-mono text-xs" value={form.tray_loja_url || ''}
            onChange={e => setForm((f: any) => ({ ...f, tray_loja_url: e.target.value }))}
            placeholder="https://minhaloja.commercesuite.com.br" />
        </div>

        <TokenInput
          label="Consumer Key"
          placeholder="seu consumer key"
          configurado={integ.tray_consumer_key_configurado}
          editando={editandoTrayKey}
          onEditar={() => setEditandoTrayKey(true)}
          valor={form.tray_consumer_key}
          onChange={(v: string) => setForm((f: any) => ({ ...f, tray_consumer_key: v }))}
        />

        <TokenInput
          label="Consumer Secret"
          placeholder="seu consumer secret"
          configurado={integ.tray_consumer_secret_configurado}
          editando={editandoTraySecret}
          onEditar={() => setEditandoTraySecret(true)}
          valor={form.tray_consumer_secret}
          onChange={(v: string) => setForm((f: any) => ({ ...f, tray_consumer_secret: v }))}
        />

        <div className="flex gap-2">
          <button onClick={() => {
            const payload: any = { tray_loja_url: form.tray_loja_url }
            if (form.tray_consumer_key !== undefined) payload.tray_consumer_key = form.tray_consumer_key
            if (form.tray_consumer_secret !== undefined) payload.tray_consumer_secret = form.tray_consumer_secret
            save.mutate(payload)
          }}
            className="btn-primary flex-1">
            <Save size={14} /> Salvar credenciais
          </button>
        </div>

        <div className="pt-3 border-t border-ink-200 flex gap-2 flex-wrap">
          {jaConectado ? (
            <>
              <button onClick={testarTray} disabled={testando} className="btn-secondary flex-1">
                {testando ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />}
                Testar conexão
              </button>
              <button onClick={sincronizarTray} disabled={sincronizando} className="btn-secondary flex-1">
                {sincronizando ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
                Sincronizar pedidos
              </button>
              <button onClick={desconectarTray} className="btn-secondary text-red-700 hover:bg-red-50">
                Desconectar
              </button>
            </>
          ) : (
            <button onClick={conectarTray} disabled={!credenciaisOk || conectando}
              className="btn-primary w-full bg-pink-500 hover:bg-pink-600 text-white border-pink-500">
              {conectando ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
              Conectar Tray
            </button>
          )}
        </div>

        {!credenciaisOk && (
          <p className="text-xs text-ink-500">
            Salve URL da loja + Consumer Key + Secret antes de conectar.
          </p>
        )}
      </div>
    </div>
  )
}


function SecaoTiny({ integ, form, setForm, save, editandoTinyToken, setEditandoTinyToken }: any) {
  const qc = useQueryClient()
  const [sincronizando, setSincronizando] = useState(false)
  const [syncEstoque, setSyncEstoque] = useState(false)

  async function syncAgora() {
    setSincronizando(true)
    try {
      const r = await api.post('/empresa/atual/integracoes/tiny/sync-agora')
      toast.success(`Sync OK: ${r.data.criadas || 0} criadas, ${r.data.atualizadas || 0} atualizadas, ${r.data.baixadas || 0} baixadas`, { duration: 5000 })
      qc.invalidateQueries({ queryKey: ['integracoes'] })
      qc.invalidateQueries({ queryKey: ['financeiro-cli'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setSincronizando(false)
    }
  }

  async function syncEstoqueAgora() {
    if (!confirm('Sync de estoque demora ~5-10min (1 chamada por produto). Continuar?')) return
    setSyncEstoque(true)
    try {
      const r = await api.post('/empresa/atual/integracoes/tiny/sync-estoque')
      toast.success(`Estoque sincronizado: ${r.data.atualizados}/${r.data.produtos} produtos`, { duration: 5000 })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    } finally {
      setSyncEstoque(false)
    }
  }

  async function toggleAtivo() {
    const novoEstado = !integ.tiny_sync_ativo
    if (!novoEstado && !confirm('Desativar sync Tiny? O worker vai parar de importar contas/produtos. Você pode reativar a qualquer momento.')) return
    try {
      await api.put('/empresa/atual/integracoes', { tiny_sync_ativo: novoEstado })
      toast.success(novoEstado ? 'Sync Tiny ativado' : 'Sync Tiny desativado')
      qc.invalidateQueries({ queryKey: ['integracoes'] })
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  const ativo = integ.tiny_sync_ativo
  const ultimaSync = integ.tiny_ultima_sync
    ? new Date(integ.tiny_ultima_sync).toLocaleString('pt-BR')
    : 'nunca'

  return (
    <div className="border border-ink-200 rounded-lg overflow-hidden">
      <div className="bg-ink-50 border-b border-ink-200 px-4 py-3 flex items-center gap-3">
        <FileText size={18} className="text-orange-600" />
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Tiny ERP</h3>
          <p className="text-xs text-ink-500">Sincronização de produtos, contas a pagar/receber e estoque</p>
          {integ.tiny_token_configurado && (
            <p className="text-xs text-ink-600 mt-0.5">
              Última sync: <span className="font-mono">{ultimaSync}</span>
            </p>
          )}
        </div>
        <span className={`text-[10px] font-semibold uppercase px-2 py-0.5 rounded ${
          !integ.tiny_token_configurado ? 'bg-ink-100 text-ink-500'
          : ativo ? 'bg-emerald-100 text-emerald-800'
          : 'bg-amber-100 text-amber-800'
        }`}>
          {!integ.tiny_token_configurado ? 'Não configurado' : ativo ? '✓ Ativo' : '⏸ Pausado'}
        </span>
      </div>

      <div className="p-4 space-y-3">
        <div className="bg-blue-50 border border-blue-200 rounded p-2 text-[11px] text-blue-900">
          💡 Cole o token v2 do Tiny (em <a href="https://tiny.com.br" target="_blank" rel="noreferrer" className="underline">tiny.com.br</a> &gt; Configurações &gt; API). Worker roda a cada 10min e sincroniza:
          <ul className="list-disc ml-5 mt-1">
            <li>Contas a pagar/receber (abertas, pagas, canceladas)</li>
            <li>Produtos com cadastro completo (CPF/CNPJ, categoria, NCM)</li>
            <li>Estoque (em breve)</li>
          </ul>
        </div>

        <TokenInput
          label="Token Tiny v2"
          placeholder="64 caracteres hexadecimais"
          configurado={integ.tiny_token_configurado}
          editando={editandoTinyToken}
          onEditar={() => setEditandoTinyToken(true)}
          valor={form.tiny_token}
          onChange={(v: string) => setForm((f: any) => ({ ...f, tiny_token: v }))}
        />

        <div className="flex gap-2">
          <button onClick={() => {
            const payload: any = {}
            if (form.tiny_token !== undefined) payload.tiny_token = form.tiny_token
            save.mutate(payload)
          }}
            className="btn-primary flex-1">
            <Save size={14} /> Salvar token
          </button>
        </div>

        {integ.tiny_token_configurado && (
          <div className="pt-3 border-t border-ink-200 space-y-2">
            <div className="flex gap-2">
              <button
                onClick={toggleAtivo}
                className={`btn-secondary flex-1 ${ativo ? 'text-amber-700' : 'text-emerald-700'}`}
              >
                {ativo ? '⏸ Desativar sync' : '▶ Reativar sync'}
              </button>
              <button onClick={syncAgora} disabled={sincronizando || !ativo} className="btn-secondary flex-1">
                {sincronizando ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
                Sincronizar contas
              </button>
            </div>
            <button onClick={syncEstoqueAgora} disabled={syncEstoque || !ativo} className="btn-secondary w-full">
              {syncEstoque ? <Loader2 size={14} className="animate-spin" /> : <RotateCw size={14} />}
              Sincronizar estoque (demora ~5-10min)
            </button>
          </div>
        )}
      </div>
    </div>
  )
}


// ============ SELETOR DE TIPO DE OPERACAO ============
const TIPOS_OPERACAO = [
  {
    id: 'completo',
    titulo: 'Completo',
    descricao: 'Empresa normal com NF obrigatória, fluxo OMS completo',
    icon: Building2,
    cor: 'blue',
    funcionalidades: ['Fluxo OMS completo', 'Emissão de NFe/NFCe', 'Separação e expedição', 'Controle fiscal rigoroso'],
    disponivel: true,
  },
  {
    id: 'mercadinho',
    titulo: 'Mercadinho / Pequeno Varejo',
    descricao: 'Venda rápida sem obrigação fiscal, foco em contas a receber',
    icon: Store,
    cor: 'emerald',
    funcionalidades: ['Venda direta sem NF', 'Fluxo simplificado', 'Contas a receber (venda a prazo)', 'Sem separação/embalagem'],
    disponivel: true,
  },
  {
    id: 'lanchonete',
    titulo: 'Lanchonete / Bar',
    descricao: 'Consumo local, comandas e contas abertas',
    icon: Utensils,
    cor: 'orange',
    funcionalidades: ['Comandas por mesa', 'Pedidos rápidos', 'Sem NF', 'Fechamento de conta'],
    disponivel: false,
  },
  {
    id: 'oficina',
    titulo: 'Oficina / Serviços',
    descricao: 'Ordem de serviço, peças + mão de obra',
    icon: Wrench,
    cor: 'amber',
    funcionalidades: ['Ordem de Serviço', 'Peças + mão de obra', 'Orçamento antes de executar', 'NFS-e'],
    disponivel: false,
  },
  {
    id: 'salao',
    titulo: 'Salão de Beleza',
    descricao: 'Agendamento de serviços, pacotes, comissões',
    icon: Scissors,
    cor: 'pink',
    funcionalidades: ['Agenda de horários', 'Pacotes de serviços', 'Comissões profissional', 'Histórico de cliente'],
    disponivel: false,
  },
]

function TipoOperacaoSelector({ tipoAtual, onChange }: { tipoAtual: string; onChange: (v: string) => void }) {
  return (
    <div className="border border-ink-200 rounded-lg p-4 bg-ink-50/30">
      <h3 className="font-semibold text-sm mb-1 flex items-center gap-2">
        <Building2 size={16} /> Tipo de operação
      </h3>
      <p className="text-xs text-ink-500 mb-3">
        Define o fluxo de vendas, cobrança fiscal e comportamento do sistema para essa empresa.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {TIPOS_OPERACAO.map((t) => {
          const Icon = t.icon
          const selecionado = tipoAtual === t.id
          const cores: Record<string, string> = {
            blue: 'border-blue-300 bg-blue-50 ring-blue-400',
            emerald: 'border-emerald-300 bg-emerald-50 ring-emerald-400',
            orange: 'border-orange-300 bg-orange-50 ring-orange-400',
            amber: 'border-amber-300 bg-amber-50 ring-amber-400',
            pink: 'border-pink-300 bg-pink-50 ring-pink-400',
          }
          const iconCores: Record<string, string> = {
            blue: 'text-blue-700 bg-blue-100',
            emerald: 'text-emerald-700 bg-emerald-100',
            orange: 'text-orange-700 bg-orange-100',
            amber: 'text-amber-700 bg-amber-100',
            pink: 'text-pink-700 bg-pink-100',
          }
          return (
            <button
              key={t.id}
              type="button"
              disabled={!t.disponivel}
              onClick={() => t.disponivel && onChange(t.id)}
              className={`text-left p-3 rounded-lg border-2 transition-all relative ${
                selecionado
                  ? `${cores[t.cor]} ring-2`
                  : t.disponivel
                    ? 'border-ink-200 bg-white hover:border-ink-300'
                    : 'border-ink-100 bg-ink-50 opacity-60 cursor-not-allowed'
              }`}
            >
              {selecionado && (
                <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center">
                  <Check size={12} className="text-white" />
                </div>
              )}
              {!t.disponivel && (
                <span className="absolute top-2 right-2 text-[10px] bg-ink-200 text-ink-600 px-1.5 py-0.5 rounded font-semibold">
                  EM BREVE
                </span>
              )}
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded ${iconCores[t.cor]}`}>
                  <Icon size={16} />
                </div>
                <span className="font-semibold text-sm text-ink-900">{t.titulo}</span>
              </div>
              <p className="text-xs text-ink-500 mb-2">{t.descricao}</p>
              <ul className="space-y-0.5">
                {t.funcionalidades.map((f, i) => (
                  <li key={i} className="text-[11px] text-ink-600 flex items-start gap-1">
                    <Check size={10} className="text-emerald-600 mt-0.5 flex-shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </button>
          )
        })}
      </div>

      <p className="text-[11px] text-ink-500 mt-3 italic">
        Alterar o tipo afeta telas e funcionalidades. Não altera dados existentes.
      </p>
    </div>
  )
}

// ============================================================================
// SEÇÃO SOFIA (IA) — provedores em cascata + memória própria
// ============================================================================
// Backend: app/core/ia_gateway.py (cascata + memória) e app/modules/ia/provedor_routes.py

interface ProvedorIA {
  provedor: string
  nome: string
  api: string
  placeholder: string
  url_painel: string
  tem_camada_gratis: boolean
  modelos: string[]
  modelo_padrao: string
  id: number | null
  configurado: boolean
  ativo: boolean
  ordem: number
  modelo: string | null
  base_url: string | null
  limite_diario: number | null
  status: string
  ultimo_erro: string | null
  em_cooldown: boolean
  falha_ate: string | null
  chamadas_total: number
  chamadas_dia: number
  falhas_total: number
  tokens_entrada: number
  tokens_saida: number
  ultima_chamada: string | null
}

interface MemoriaIA {
  id: number
  finalidade: string
  escopo: string
  pergunta: string
  resposta: string
  origem: string
  provedor: string | null
  modelo: string | null
  hits: number
  fixado: boolean
  ativo: boolean
  expirada: boolean
  expira_em: string | null
  criado_em: string
  usado_em: string | null
}

const FINALIDADES: Record<string, string> = {
  chat: 'Chat da Sofia',
  sac_classificacao: 'SAC — classificação',
  sac_resposta: 'SAC — resposta',
  servicos_diagnostico: 'Serviços — diagnóstico',
}

function SecaoSofiaIA() {
  const qc = useQueryClient()
  const [aba, setAba] = useState<'provedores' | 'memoria'>('provedores')

  const { data: provedores, isLoading } = useQuery<ProvedorIA[]>({
    queryKey: ['ia-provedores'],
    queryFn: async () => (await api.get('/ia/provedores')).data,
  })
  const { data: cfg } = useQuery<any>({
    queryKey: ['ia-config'],
    queryFn: async () => (await api.get('/ia/config')).data,
  })
  const { data: stats } = useQuery<any>({
    queryKey: ['ia-stats'],
    queryFn: async () => (await api.get('/ia/memoria/estatisticas?dias=30')).data,
  })

  const configurados = (provedores || []).filter(p => p.configurado)
  const prontos = configurados.filter(p => p.ativo && !p.em_cooldown)

  function invalidar() {
    qc.invalidateQueries({ queryKey: ['ia-provedores'] })
    qc.invalidateQueries({ queryKey: ['ia-config'] })
    qc.invalidateQueries({ queryKey: ['ia-stats'] })
  }

  return (
    <SecaoIntegracao
      icone={<Bot size={18} className="text-purple-600" />}
      titulo="Sofia (IA) — provedores e memória"
      descricao="Várias chaves de IA em cascata + memória própria que reduz o consumo de API"
      conectado={configurados.length > 0}
      info={configurados.length
        ? `${configurados.length} chave(s) cadastrada(s) · ${prontos.length} pronta(s) agora`
        : undefined}
    >
      {/* ---------- O TEXTINHO: como isso funciona ---------- */}
      <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
        <div className="flex items-start gap-2">
          <Info size={16} className="text-purple-600 mt-0.5 shrink-0" />
          <div className="text-xs text-purple-900 space-y-1.5 leading-relaxed">
            <p className="font-semibold text-sm">Como a Sofia economiza API</p>
            <p>
              Cadastre <strong>quantas chaves quiser</strong> (Gemini, Grok, Claude, GPT, DeepSeek,
              Groq, Mistral, OpenRouter). A cada pergunta a Sofia segue sempre esta ordem:
            </p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>
                <strong>Olha primeiro na memória própria.</strong> Se já respondeu algo parecido
                antes e a resposta ainda vale, devolve na hora — <em>sem gastar nenhuma API</em>.
              </li>
              <li>
                <strong>Se não achou, consome os provedores um por um</strong>, na ordem da lista
                abaixo. Acabou a cota do primeiro, deu erro na chave, caiu o serviço? Ela passa
                automaticamente pro próximo, e pro próximo, até alguém responder. O provedor que
                falhou fica alguns minutos de fora e volta sozinho pra fila depois.
              </li>
              <li>
                <strong>Grava a resposta na memória</strong> e aprende. Da próxima vez que
                perguntarem a mesma coisa, sai da memória, de graça.
              </li>
            </ol>
            <p>
              É assim que o consumo vai <strong>caindo com o tempo</strong>: quanto mais a empresa
              usa, mais a Sofia já sabe, e menos ela precisa pagar pra perguntar fora. Você também
              pode <strong>ensinar ela na mão</strong> (aba Memória) — procedimento interno, política
              da loja, jargão do ramo. Isso nunca gasta API.
            </p>
            <p className="text-[11px] text-purple-700 border-t border-purple-200 pt-1.5">
              Resposta que cita <strong>números da empresa</strong> (vendas, estoque, saldos) fica
              válida só enquanto os dados não mudarem — se a venda do dia muda, a Sofia consulta de
              novo. Já resposta <strong>explicativa</strong> ("como emitir uma NFe?") vale pra sempre.
            </p>
          </div>
        </div>
      </div>

      {/* ---------- placar da economia ---------- */}
      {stats && stats.consultas_total > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          <PlacarIA valor={`${stats.economia_percentual}%`} rotulo="sem gastar API" destaque />
          <PlacarIA valor={stats.respondidas_pela_memoria} rotulo="pela memória" />
          <PlacarIA valor={stats.respondidas_pela_api} rotulo="pela API" />
          <PlacarIA valor={stats.memoria_itens} rotulo="coisas aprendidas" />
        </div>
      )}

      {/* ---------- abas ---------- */}
      <div className="flex gap-1 border-b border-ink-200 mb-3">
        {([['provedores', 'Provedores (cascata)'], ['memoria', 'Memória própria']] as const).map(([k, t]) => (
          <button key={k} type="button" onClick={() => setAba(k)}
            className={`px-3 py-1.5 text-xs font-medium -mb-px border-b-2 ${
              aba === k ? 'border-purple-600 text-purple-700' : 'border-transparent text-ink-500 hover:text-ink-700'
            }`}>{t}</button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : aba === 'provedores' ? (
        <ListaProvedores provedores={provedores || []} onMudou={invalidar} />
      ) : (
        <AbaMemoria cfg={cfg} onMudou={invalidar} stats={stats} />
      )}
    </SecaoIntegracao>
  )
}

function PlacarIA({ valor, rotulo, destaque }: any) {
  return (
    <div className={`rounded-lg px-3 py-2 border ${
      destaque ? 'bg-emerald-50 border-emerald-200' : 'bg-ink-50 border-ink-200'
    }`}>
      <div className={`text-lg font-bold leading-none ${destaque ? 'text-emerald-700' : 'text-ink-800'}`}>{valor}</div>
      <div className="text-[10px] uppercase tracking-wide text-ink-500 mt-1">{rotulo}</div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Lista de provedores (a cascata)
// ---------------------------------------------------------------------------
function ListaProvedores({ provedores, onMudou }: { provedores: ProvedorIA[], onMudou: () => void }) {
  const cadastrados = provedores.filter(p => p.configurado).sort((a, b) => a.ordem - b.ordem)
  const disponiveis = provedores.filter(p => !p.configurado)

  async function mover(slug: string, dir: -1 | 1) {
    const ordem = cadastrados.map(p => p.provedor)
    const i = ordem.indexOf(slug)
    const j = i + dir
    if (i < 0 || j < 0 || j >= ordem.length) return
    ;[ordem[i], ordem[j]] = [ordem[j], ordem[i]]
    try {
      await api.post('/ia/provedores/reordenar', { ordem })
      onMudou()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao reordenar')
    }
  }

  return (
    <div className="space-y-3">
      {cadastrados.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-ink-700 mb-1.5">
            Ordem de consumo <span className="font-normal text-ink-500">— o nº 1 é usado primeiro; os outros são o plano B</span>
          </p>
          <div className="space-y-2">
            {cadastrados.map((p, i) => (
              <CartaoProvedor key={p.provedor} p={p} posicao={i + 1} onMudou={onMudou}
                podeSubir={i > 0} podeDescer={i < cadastrados.length - 1}
                onSubir={() => mover(p.provedor, -1)} onDescer={() => mover(p.provedor, 1)} />
            ))}
          </div>
        </div>
      )}

      {cadastrados.length === 0 && (
        <div className="text-center py-4 text-xs text-ink-500 bg-ink-50 rounded-lg border border-dashed border-ink-300">
          Nenhuma chave cadastrada ainda. Comece pelas gratuitas (Gemini, Groq) — já dá pra rodar a Sofia sem custo.
        </div>
      )}

      {disponiveis.length > 0 && (
        <div>
          <p className="text-xs font-semibold text-ink-700 mb-1.5 mt-3">Adicionar provedor</p>
          <div className="space-y-2">
            {disponiveis.map(p => (
              <CartaoProvedor key={p.provedor} p={p} posicao={null} onMudou={onMudou} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

const BADGE_STATUS: Record<string, { txt: string, cls: string }> = {
  ok: { txt: 'OK', cls: 'bg-emerald-100 text-emerald-800' },
  esgotado: { txt: 'Sem cota', cls: 'bg-amber-100 text-amber-800' },
  erro: { txt: 'Erro', cls: 'bg-red-100 text-red-800' },
  nao_configurado: { txt: 'Sem chave', cls: 'bg-ink-100 text-ink-600' },
}

function CartaoProvedor({ p, posicao, onMudou, podeSubir, podeDescer, onSubir, onDescer }: any) {
  const [aberto, setAberto] = useState(!p.configurado ? false : false)
  const [chave, setChave] = useState('')
  const [modelo, setModelo] = useState(p.modelo || '')
  const [limite, setLimite] = useState<string>(p.limite_diario ? String(p.limite_diario) : '')
  const [salvando, setSalvando] = useState(false)
  const [testando, setTestando] = useState(false)

  const badge = BADGE_STATUS[p.status] || BADGE_STATUS.nao_configurado

  async function salvar() {
    setSalvando(true)
    try {
      const payload: any = { modelo, limite_diario: limite ? Number(limite) : 0 }
      if (chave.trim()) payload.api_key = chave.trim()
      await api.put(`/ia/provedores/${p.provedor}`, payload)
      toast.success(`${p.nome} salvo`)
      setChave('')
      setAberto(false)
      onMudou()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar')
    } finally { setSalvando(false) }
  }

  async function alternarAtivo() {
    try {
      await api.put(`/ia/provedores/${p.provedor}`, { ativo: !p.ativo })
      onMudou()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function testar() {
    setTestando(true)
    try {
      const r = await api.post(`/ia/provedores/${p.provedor}/testar`)
      if (r.data.ok) toast.success(`${p.nome} respondeu (${r.data.modelo})`)
      else toast.error(`${p.nome}: ${r.data.erro}`)
      onMudou()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro no teste')
    } finally { setTestando(false) }
  }

  function remover() {
    pedirExclusao({
      titulo: 'Remover chave de IA',
      descricao: `a chave do ${p.nome}`,
      onConfirm: async () => {
        try {
          await api.delete(`/ia/provedores/${p.provedor}`)
          toast.success('Chave removida')
          onMudou()
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Erro')
        }
      },
    })
  }

  return (
    <div className={`border rounded-lg ${p.configurado && !p.ativo ? 'border-ink-200 bg-ink-50 opacity-70' : 'border-ink-200'}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        {posicao !== null && (
          <span className="w-6 h-6 shrink-0 rounded-full bg-purple-100 text-purple-700 text-xs font-bold flex items-center justify-center">
            {posicao}
          </span>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-sm font-medium">{p.nome}</span>
            {p.tem_camada_gratis && (
              <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">
                tem plano grátis
              </span>
            )}
            {p.configurado && (
              <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${badge.cls}`}>
                {badge.txt}
              </span>
            )}
            {p.em_cooldown && (
              <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                fora da fila por alguns min
              </span>
            )}
          </div>
          <p className="text-[11px] text-ink-500 truncate">
            {p.configurado
              ? <>{p.modelo || p.modelo_padrao} · {p.chamadas_total} chamada(s){p.chamadas_dia ? ` · ${p.chamadas_dia} hoje` : ''}{p.falhas_total ? ` · ${p.falhas_total} falha(s)` : ''}</>
              : <>Modelo padrão: {p.modelo_padrao}</>}
          </p>
          {p.configurado && p.ultimo_erro && (
            <p className="text-[11px] text-red-600 truncate" title={p.ultimo_erro}>
              Último erro: {p.ultimo_erro}
            </p>
          )}
        </div>

        {p.configurado && posicao !== null && (
          <div className="flex flex-col shrink-0">
            <button type="button" disabled={!podeSubir} onClick={onSubir}
              className="px-1 text-ink-400 hover:text-ink-700 disabled:opacity-20" title="Usar antes">▲</button>
            <button type="button" disabled={!podeDescer} onClick={onDescer}
              className="px-1 text-ink-400 hover:text-ink-700 disabled:opacity-20" title="Usar depois">▼</button>
          </div>
        )}
        {p.configurado && (
          <button type="button" onClick={alternarAtivo}
            className={`shrink-0 text-[10px] font-semibold uppercase px-2 py-1 rounded ${
              p.ativo ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200' : 'bg-ink-200 text-ink-600 hover:bg-ink-300'
            }`} title={p.ativo ? 'Desligar deste provedor' : 'Religar'}>
            {p.ativo ? 'ativo' : 'off'}
          </button>
        )}
        <button type="button" onClick={() => setAberto(a => !a)}
          className="shrink-0 px-2 py-1 text-xs bg-ink-100 hover:bg-ink-200 rounded">
          {p.configurado ? 'Editar' : 'Cadastrar'}
        </button>
      </div>

      {aberto && (
        <div className="border-t border-ink-200 px-3 py-3 space-y-2 bg-ink-50/50">
          <div>
            <label className="label">
              Chave de API {p.configurado && <span className="text-xs text-emerald-600">(já cadastrada — deixe vazio pra manter)</span>}
            </label>
            <input className="input font-mono text-xs" type="password" autoComplete="new-password"
              value={chave} onChange={e => setChave(e.target.value)} placeholder={p.placeholder} />
            {p.url_painel && (
              <p className="text-xs text-ink-500 mt-1">
                Pegue em <a href={p.url_painel} target="_blank" rel="noreferrer"
                  className="text-brand-600 hover:underline">{p.url_painel.replace(/^https?:\/\//, '')}</a>
              </p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="label">Modelo</label>
              <select className="input" value={modelo} onChange={e => setModelo(e.target.value)}>
                <option value="">Padrão ({p.modelo_padrao})</option>
                {p.modelos.map((m: string) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Limite de chamadas/dia</label>
              <input className="input" type="number" min={0} value={limite}
                onChange={e => setLimite(e.target.value)} placeholder="sem limite" />
              <p className="text-[11px] text-ink-500 mt-0.5">Ao bater o limite, a Sofia pula pro próximo.</p>
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button type="button" onClick={salvar} disabled={salvando} className="btn-primary flex-1">
              {salvando ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Salvar
            </button>
            {p.configurado && (
              <>
                <button type="button" onClick={testar} disabled={testando}
                  className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded flex items-center gap-1">
                  {testando ? <Loader2 size={14} className="animate-spin" /> : <TestTube size={14} />} Testar
                </button>
                <button type="button" onClick={remover}
                  className="px-3 py-1.5 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded flex items-center gap-1">
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Aba memória
// ---------------------------------------------------------------------------
function AbaMemoria({ cfg, onMudou, stats }: any) {
  const qc = useQueryClient()
  const [busca, setBusca] = useState('')
  const [ensinando, setEnsinando] = useState(false)
  const [novaP, setNovaP] = useState('')
  const [novaR, setNovaR] = useState('')
  const [novaFinalidade, setNovaFinalidade] = useState('chat')
  const [editandoId, setEditandoId] = useState<number | null>(null)
  const [textoEdit, setTextoEdit] = useState('')

  const [memAtiva, setMemAtiva] = useState<boolean>(cfg?.memoria_ativa ?? true)
  const [limiar, setLimiar] = useState<number>(cfg?.limiar ?? 0.82)
  const [ttl, setTtl] = useState<number>(cfg?.ttl_horas ?? 12)

  useEffect(() => {
    if (!cfg) return
    setMemAtiva(cfg.memoria_ativa)
    setLimiar(cfg.limiar)
    setTtl(cfg.ttl_horas)
  }, [cfg])

  const { data: itens, isLoading } = useQuery<MemoriaIA[]>({
    queryKey: ['ia-memoria', busca],
    queryFn: async () => (await api.get('/ia/memoria', { params: { busca: busca || undefined, limite: 100 } })).data,
  })

  function recarregar() {
    qc.invalidateQueries({ queryKey: ['ia-memoria'] })
    onMudou()
  }

  async function salvarCfg(patch: any) {
    try {
      await api.put('/ia/config', patch)
      toast.success('Memória atualizada')
      onMudou()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function ensinar() {
    if (!novaP.trim() || !novaR.trim()) return toast.error('Preencha a pergunta e a resposta')
    try {
      await api.post('/ia/memoria', {
        pergunta: novaP.trim(), resposta: novaR.trim(),
        finalidade: novaFinalidade, escopo: 'faq', fixado: true,
      })
      toast.success('Sofia aprendeu — essa não gasta API nunca mais')
      setNovaP(''); setNovaR(''); setEnsinando(false)
      recarregar()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao ensinar')
    }
  }

  async function corrigir(id: number) {
    try {
      await api.patch(`/ia/memoria/${id}`, { resposta: textoEdit })
      toast.success('Resposta corrigida — a Sofia passa a usar a sua versão')
      setEditandoId(null)
      recarregar()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  function apagar(id: number) {
    pedirExclusao({
      titulo: 'Apagar da memória',
      descricao: 'esse item que a Sofia aprendeu',
      onConfirm: async () => {
        try {
          await api.delete(`/ia/memoria/${id}`)
          recarregar()
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Erro')
        }
      },
    })
  }

  function limpar(tudo: boolean) {
    pedirExclusao({
      titulo: tudo ? 'Zerar o aprendizado da Sofia' : 'Limpar memória vencida',
      descricao: tudo
        ? 'tudo que a Sofia aprendeu (os itens ensinados à mão são preservados)'
        : 'os itens de memória já vencidos',
      confirmLabel: tudo ? 'Zerar' : 'Limpar',
      onConfirm: async () => {
        try {
          const r = await api.post('/ia/memoria/limpar', null, { params: { tudo, expiradas: !tudo } })
          toast.success(`${r.data.removidas} item(ns) removido(s)`)
          recarregar()
        } catch (e: any) {
          toast.error(e?.response?.data?.detail || 'Erro')
        }
      },
    })
  }

  return (
    <div className="space-y-3">
      {/* config */}
      <div className="border border-ink-200 rounded-lg p-3 space-y-2">
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={memAtiva}
            onChange={e => { setMemAtiva(e.target.checked); salvarCfg({ memoria_ativa: e.target.checked }) }} />
          <span className="text-sm font-medium">Memória própria ativa</span>
          <span className="text-xs text-ink-500">— desligando, toda pergunta volta a gastar API</span>
        </label>

        <div className="grid grid-cols-2 gap-3 pt-1">
          <div>
            <label className="label">Sensibilidade ({limiar.toFixed(2)})</label>
            <input type="range" min={0.5} max={0.98} step={0.01} value={limiar} className="w-full"
              onChange={e => setLimiar(Number(e.target.value))}
              onMouseUp={() => salvarCfg({ limiar })} onTouchEnd={() => salvarCfg({ limiar })} />
            <p className="text-[11px] text-ink-500">
              Mais baixo = reaproveita mais (economiza mais, porém pode casar perguntas diferentes).
              Mais alto = só reaproveita pergunta quase idêntica.
            </p>
          </div>
          <div>
            <label className="label">Validade das respostas com números</label>
            <div className="flex gap-2">
              <input className="input" type="number" min={1} max={8760} value={ttl}
                onChange={e => setTtl(Number(e.target.value))} />
              <button type="button" onClick={() => salvarCfg({ ttl_horas: ttl })}
                className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded whitespace-nowrap">
                horas
              </button>
            </div>
            <p className="text-[11px] text-ink-500">
              Respostas explicativas não vencem — só as que citam valores da empresa.
            </p>
          </div>
        </div>
      </div>

      {/* ensinar */}
      {!ensinando ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => setEnsinando(true)} className="btn-primary">
            <Sparkles size={14} /> Ensinar a Sofia
          </button>
          <button type="button" onClick={() => limpar(false)}
            className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded">Limpar vencidos</button>
          <button type="button" onClick={() => limpar(true)}
            className="px-3 py-1.5 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded">Zerar aprendizado</button>
        </div>
      ) : (
        <div className="border border-purple-200 bg-purple-50 rounded-lg p-3 space-y-2">
          <p className="text-xs text-purple-900">
            O que você escrever aqui a Sofia passa a responder <strong>sem consultar nenhuma API</strong>.
            Bom pra: procedimento interno, política de troca, prazo de entrega, jargão do seu ramo.
          </p>
          <div>
            <label className="label">Onde vale</label>
            <select className="input" value={novaFinalidade} onChange={e => setNovaFinalidade(e.target.value)}>
              {Object.entries(FINALIDADES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Pergunta (como o pessoal costuma perguntar)</label>
            <input className="input" value={novaP} onChange={e => setNovaP(e.target.value)}
              placeholder="Ex: qual o prazo de garantia das peças?" />
          </div>
          <div>
            <label className="label">Resposta que a Sofia deve dar</label>
            <textarea className="input" rows={4} value={novaR} onChange={e => setNovaR(e.target.value)}
              placeholder="Ex: Peças têm 90 dias de garantia contra defeito de fabricação, contados da data da NF..." />
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={ensinar} className="btn-primary flex-1"><Save size={14} /> Ensinar</button>
            <button type="button" onClick={() => setEnsinando(false)}
              className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded">Cancelar</button>
          </div>
        </div>
      )}

      {/* busca + lista */}
      <input className="input" value={busca} onChange={e => setBusca(e.target.value)}
        placeholder="Buscar no que a Sofia já sabe..." />

      {isLoading ? (
        <div className="flex justify-center py-6"><Loader2 className="animate-spin text-ink-400" /></div>
      ) : !itens?.length ? (
        <div className="text-center py-5 text-xs text-ink-500 bg-ink-50 rounded-lg border border-dashed border-ink-300">
          {busca
            ? 'Nada encontrado.'
            : 'A memória está vazia. Ela se enche sozinha conforme o pessoal usa a Sofia — ou ensine você mesmo acima.'}
        </div>
      ) : (
        <div className="space-y-2">
          {itens.map(m => (
            <div key={m.id} className={`border rounded-lg px-3 py-2 ${
              !m.ativo || m.expirada ? 'border-ink-200 bg-ink-50 opacity-70' : 'border-ink-200'
            }`}>
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{m.pergunta}</p>
                  {editandoId === m.id ? (
                    <div className="mt-1.5 space-y-1.5">
                      <textarea className="input" rows={4} value={textoEdit}
                        onChange={e => setTextoEdit(e.target.value)} />
                      <div className="flex gap-2">
                        <button type="button" onClick={() => corrigir(m.id)} className="btn-primary">
                          <Save size={14} /> Salvar correção
                        </button>
                        <button type="button" onClick={() => setEditandoId(null)}
                          className="px-3 py-1.5 text-xs bg-ink-100 hover:bg-ink-200 rounded">Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-xs text-ink-600 whitespace-pre-wrap mt-0.5 line-clamp-4">{m.resposta}</p>
                  )}
                  <div className="flex items-center gap-1.5 flex-wrap mt-1">
                    <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-ink-100 text-ink-600">
                      {FINALIDADES[m.finalidade] || m.finalidade}
                    </span>
                    <span className={`text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded ${
                      m.escopo === 'faq' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'
                    }`} title={m.escopo === 'faq' ? 'Vale pra sempre' : 'Vale enquanto os dados não mudarem'}>
                      {m.escopo === 'faq' ? 'atemporal' : 'com dados'}
                    </span>
                    {m.origem === 'manual' && (
                      <span className="text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                        ensinado
                      </span>
                    )}
                    {m.hits > 0 && (
                      <span className="text-[10px] text-emerald-700 font-medium">
                        {m.hits}× reaproveitada (API não gasta)
                      </span>
                    )}
                    {m.expirada && <span className="text-[10px] text-amber-700">vencida</span>}
                    {m.provedor && <span className="text-[10px] text-ink-400">aprendeu com {m.provedor}</span>}
                  </div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button type="button" title="Corrigir a resposta"
                    onClick={() => { setEditandoId(m.id); setTextoEdit(m.resposta) }}
                    className="px-2 py-1 text-xs bg-ink-100 hover:bg-ink-200 rounded">Corrigir</button>
                  <button type="button" onClick={() => apagar(m.id)}
                    className="px-2 py-1 text-xs bg-red-50 text-red-700 hover:bg-red-100 rounded">
                    <Trash2 size={12} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {stats?.por_provedor?.length > 0 && (
        <div className="text-[11px] text-ink-500 border-t border-ink-200 pt-2">
          Últimos 30 dias por provedor: {stats.por_provedor.map((x: any) => `${x.provedor} (${x.chamadas})`).join(' · ')}
          {stats.consultas_sem_resposta > 0 && ` · ${stats.consultas_sem_resposta} consulta(s) sem nenhum provedor disponível`}
        </div>
      )}
    </div>
  )
}
